import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/buttons';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { useToggle } from '@/hooks/common';
import { useNavigation } from '@/hooks/navigation';
import { DAILY_LIMIT, useScratchGameLootbox } from '@/modules/promotions';
import { useGetCommonStore } from '@/store';
import { ScFortuneWheelPromoSection } from '../FortuneWheelPromoSection/styled';

export interface IScratchPromoSection {
  onLinkClick?: () => void;
}

export const ScratchPromoSection: FC<IScratchPromoSection> = ({
  onLinkClick,
}) => {
  const { navigate } = useNavigation();
  const { t } = useTranslation('scratchGame');

  const [isFullInfoOpen, toggleFullInfo] = useToggle();

  const useCommonStore = useGetCommonStore();

  const scratchGameMenuData = useCommonStore.use.scratchGameMenuData();
  const { dailyActivatedLootboxes, isReachedDailyLimit } =
    useScratchGameLootbox();

  const handleNavigateToQuest = useCallback(() => {
    navigate('/scratch-card');
  }, [navigate]);

  const labelDescription = useMemo(() => {
    if (isReachedDailyLimit) {
      return scratchGameMenuData?.menu?.tryTomorrow;
    }

    return (
      <span>
        <strong>{scratchGameMenuData?.menu?.dailySpins}</strong>{' '}
        {dailyActivatedLootboxes}/{DAILY_LIMIT}
      </span>
    );
  }, [isReachedDailyLimit, dailyActivatedLootboxes, scratchGameMenuData]);

  return (
    <ScFortuneWheelPromoSection
      onClick={toggleFullInfo}
      showFullInfo={isFullInfoOpen}
    >
      <div className="info">
        <div className="level-info">
          <div className="text">
            <span>
              <b className="reward">{scratchGameMenuData?.menu?.reward}</b>
            </span>
            <span>{labelDescription}</span>
          </div>
          <div className="addons">
            <ImageComponent
              alt="character"
              height={44}
              src={scratchGameMenuData?.menu?.bg?.url || ''}
              width={44}
            />
            <IconComponent name="whiteArrowRight" size="18px" />
          </div>
        </div>
      </div>
      {isFullInfoOpen && (
        <Button
          navigation
          onClick={() => {
            onLinkClick && onLinkClick();
            handleNavigateToQuest();
          }}
        >
          {t('menuLink')}
        </Button>
      )}
    </ScFortuneWheelPromoSection>
  );
};
